import { Box } from "@mui/system";
import { Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleXmark } from "@fortawesome/sharp-duotone-light-svg-icons";
import Modal from "@mui/material/Modal";
import React, { useState } from "react";
import {
    useAddHistoricalForecastMutation, useDeleteHistoricalForecastMutation,
    useGetHistoricalForecastsQuery
} from "../../services/riskSurface";
import {
    ADD_HISTORICAL,
    ADD_NEW_HISTORICAL, DELETE, EXISTING_FORECAST, HISTORICAL_ERROR,
    HISTORICAL_FORECASTS,
    HISTORICAL_RULES, MAX_HISTORICAL_FORECASTS,
    REFETCH_FORECAST
} from "../UserVisibleStrings";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { SerializedError } from "@reduxjs/toolkit";
interface CustomFormProps
{
    label: string;
    name: string;
    value: string;
    onChange: ( e: React.ChangeEvent<HTMLInputElement> ) => void;
}
const CustomFormInput = ( { label, name, value, onChange }: CustomFormProps ) => (
    <TextField
        sx = {{ padding: "10px" }}
        label = {label}
        name = {name}
        value = {value}
        onChange = {onChange}
        fullWidth
        size = "small"
        variant = "outlined"
    />
);
export interface HistoricalForecastsModalProps
{
    open: boolean;
    onClose: () => void;
}

function alertStatus( msg: {data: string} | {error: FetchBaseQueryError | SerializedError} )
{
    if ( "error" in msg )
    {
        alert( "Errored request : " + JSON.stringify( msg ) );
    }
    else
    {
        alert( "Success" );
    }
}


export function HistoricalForecastsModal( props: HistoricalForecastsModalProps )
{
    const {
        data,
        isFetching,
        refetch,
        isError
    } = useGetHistoricalForecastsQuery( );
    const [ addForecast ] = useAddHistoricalForecastMutation();
    const [ deleteForecast ] = useDeleteHistoricalForecastMutation();
    const [ isSubmittingAddition, setIsSubmittingAddition ] = useState<boolean>( false );
    const [ formData, setFormData ] = useState( {
        year: "",
        month: "",
        day: "",
        hour: ""
    } );
    const handleChange = ( e: React.ChangeEvent<HTMLInputElement> ) =>
    {
        const { name, value } = e.target;
        setFormData( ( prevData ) => ( {
            ...prevData,
            [name]: value,
        } ) );
    };
    function validateInputs( year: number, month: number, day: number, hour: number ): string | undefined
    {
        if ( isNaN( year ) || year < 2020 || year > 9999 ) // NOAA NBM data on AWS only goes back to 2020
        {
            return "Invalid year";
        }
        if ( isNaN( month ) || month < 1 || month > 12 )
        {
            return "Invalid month";
        }
        if ( isNaN( day ) || day < 1 || day > 31 )
        {
            return "Invalid day";
        }
        if ( isNaN( hour ) || hour < 0 || hour > 23 )
        {
            return "Invalid hour";
        }
        return undefined;
    }
    const onAddForecastClick = ( ) =>
    {
        setIsSubmittingAddition( true );
        const year = Number( formData.year );
        const month = Number( formData.month );
        const day = Number( formData.day );
        const hour = Number( formData.hour );
        const error = validateInputs( year, month, day, hour );
        if ( error !== undefined )
        {
            alert( error );
            setIsSubmittingAddition( false );
            return;
        }
        addForecast( { year, month, day, hour } )
            .then( alertStatus )
            .catch( err => console.error( err ) )
            .finally( () =>
            {
                setIsSubmittingAddition( false );
                setFormData( {
                    year: "",
                    month: "",
                    day: "",
                    hour: ""
                } );
            } );
    };

    return (
        <Modal open = {props.open} onClose = {props.onClose}>

            <Box sx =
                {
                    {
                        width: "50%",
                        height: "90%",
                        background: "#F1F5F4",
                        position: "fixed",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        overflowY: "auto"
                    }
                }
            >
                <Stack direction = "row" spacing = {2} justifyContent = "space-between" alignItems = "center"
                    sx = {{ width: "100%" }}
                >
                    <Box sx = {{ flexGrow: 1, textAlign: "center" }}>
                        <Typography variant = "h3">{HISTORICAL_FORECASTS}</Typography>
                    </Box>
                    <Box>
                        <Button onClick = {() =>
                        {
                            props.onClose();
                        }}
                        >
                            <FontAwesomeIcon icon = {faCircleXmark} size = "2x" fixedWidth/>
                        </Button>
                    </Box>
                </Stack>
                <div style = {{ width: "100%" }} id = "forecasts">
                    {data !== undefined && data.forecastDateTime.length < MAX_HISTORICAL_FORECASTS &&
                    <Box sx = {{ padding: "15px", border: 1 }}>
                        <Typography variant = "h6">{ADD_HISTORICAL}</Typography>
                        <span>{HISTORICAL_RULES}</span>
                        <CustomFormInput
                            label = "Year (4 digits)"
                            name = "year"
                            value = {formData.year}
                            onChange = {handleChange}
                        />
                        <CustomFormInput
                            label = "Month (1-12)"
                            name = "month"
                            value = {formData.month}
                            onChange = {handleChange}
                        />
                        <CustomFormInput
                            label = "Day of month (1-31)"
                            name = "day"
                            value = {formData.day}
                            onChange = {handleChange}
                        />
                        <CustomFormInput
                            label = "Hour (0-23, Zulu)"
                            name = "hour"
                            value = {formData.hour}
                            onChange = {handleChange}
                        />
                        <Button disabled = {isSubmittingAddition} onClick = {onAddForecastClick}>
                            {ADD_NEW_HISTORICAL}
                        </Button>
                    </Box>}

                    <Box sx = {{ padding: "15px", border: 1 }}>
                        <div>
                            <Stack direction = "row" spacing = {2} justifyContent = "space-between" alignItems = "center" sx = {{ width: "100%" }}>
                                <Typography variant = "h6">{EXISTING_FORECAST} (Max {MAX_HISTORICAL_FORECASTS})</Typography>
                                <Button onClick = {() =>
                                {
                                    refetch().catch( err => console.error( err ) );
                                }}
                                >
                                    {REFETCH_FORECAST}
                                </Button>
                            </Stack>
                            {data?.forecastDateTime.map( ( each, index ) =>
                            {
                                return (
                                    <div key = {"forecastTime_" + index}>
                                        <span>{each.year}-{each.month}-{each.day} {each.hour}Z</span>
                                        <Button onClick = {() =>
                                        {
                                            deleteForecast( { year: each.year, month: each.month, day: each.day, hour: each.hour } )
                                                .then( alertStatus )
                                                .then( () => refetch().catch( err => console.error( err ) ) )
                                                .catch( err => console.error( err ) );
                                        }}
                                        >
                                            {DELETE}
                                        </Button>
                                    </div>
                                );
                            } )}
                            {isFetching && <CircularProgress/>}
                        </div>
                        {isError && <span>{HISTORICAL_ERROR}</span>}
                    </Box>
                </div>
            </Box>
        </Modal>
    );
}
